/**
 * Object Type Sync Hook
 * Updates all objects when their type definition changes (color, icon)
 */

import { useEffect, useRef } from 'react'
import { dia, shapes } from '@joint/core'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'

export function useObjectTypeSync(graph: dia.Graph | null, paper: dia.Paper | null) {
  const types = useObjectTypeStore(state => state.types)
  const mappings = useObjectTypeStore(state => state.mappings)

  // Track previous types to detect changes
  const prevTypesRef = useRef<Map<string, { color?: string; icon?: string; iconWidth?: number; iconHeight?: number; colorMode?: string; strokeColor?: string; fillColor?: string; opacity?: number; strokeWidth?: number }>>(new Map())
  // Index: typeId → Set<element.id> for O(1) lookup instead of full cell iteration
  const typeElementIndexRef = useRef<Map<string, Set<string>>>(new Map())

  // Build element index once when graph or mappings change
  useEffect(() => {
    if (!graph) return

    const index = new Map<string, Set<string>>()
    const entityToTypeMap = new Map<string, string>()
    mappings.forEach(m => entityToTypeMap.set(m.entityHandle, m.typeId))

    const cells = graph.getCells()
    cells.forEach(cell => {
      if (!cell.isElement()) return
      const elementId = cell.id as string
      const objectTypeId = cell.prop('objectTypeId')
      const entityHandle = cell.prop('data/entityHandle')

      let typeId: string | undefined
      if (objectTypeId) {
        typeId = objectTypeId
      } else if (entityHandle) {
        typeId = entityToTypeMap.get(entityHandle)
      }

      if (typeId) {
        if (!index.has(typeId)) index.set(typeId, new Set())
        index.get(typeId)!.add(elementId)
      }
    })

    typeElementIndexRef.current = index
  }, [graph, mappings])

  useEffect(() => {
    if (!graph || !paper) return

    // Build current types map
    const currentTypesMap = new Map(
      types.map(type => [type.id, {
        color: type.color, icon: type.icon, iconWidth: type.iconWidth, iconHeight: type.iconHeight,
        colorMode: type.colorMode, strokeColor: type.strokeColor, fillColor: type.fillColor,
        opacity: type.opacity, strokeWidth: type.strokeWidth
      }])
    )

    // Only process types that actually changed
    const changedTypeIds: string[] = []
    types.forEach(type => {
      const prevType = prevTypesRef.current.get(type.id)
      const currentType = currentTypesMap.get(type.id)

      if (!currentType) return

      if (!prevType) {
        changedTypeIds.push(type.id)
      } else {
        const changed = prevType.color !== currentType.color ||
          prevType.icon !== currentType.icon ||
          prevType.iconWidth !== currentType.iconWidth || prevType.iconHeight !== currentType.iconHeight ||
          prevType.colorMode !== currentType.colorMode ||
          prevType.strokeColor !== currentType.strokeColor || prevType.fillColor !== currentType.fillColor ||
          prevType.opacity !== currentType.opacity || prevType.strokeWidth !== currentType.strokeWidth
        if (changed) {
          changedTypeIds.push(type.id)
        }
      }
    })

    // Only iterate elements belonging to changed types
    if (changedTypeIds.length > 0) {
      for (const typeId of changedTypeIds) {
        const type = types.find(t => t.id === typeId)
        if (!type) continue

        const elementIds = typeElementIndexRef.current.get(typeId)
        if (!elementIds || elementIds.size === 0) {
          // Fallback: full scan for this type only
          updateElementsForType(graph, paper, typeId, type, mappings)
          continue
        }

        // Only update elements with matching typeId
        for (const elementId of elementIds) {
          const cell = graph.getCell(elementId)
          if (!cell || !cell.isElement()) continue
          updateSingleElement(cell as dia.Element, type)
        }
      }
    }

    // Update ref for next comparison
    prevTypesRef.current = currentTypesMap
  }, [graph, paper, types, mappings])
}

/**
 * Update all elements that belong to a specific object type
 */
function updateElementsForType(
  graph: dia.Graph,
  _paper: dia.Paper,
  typeId: string,
  type: { name: string; color?: string; icon?: string; iconWidth?: number; iconHeight?: number; colorMode?: string; strokeColor?: string; fillColor?: string; opacity?: number; strokeWidth?: number },
  mappings: Array<{ entityHandle: string; typeId: string }>
) {
  const cells = graph.getCells()
  let updatedCount = 0
  let colorUpdates = 0
  let imageUpdates = 0
  let typeTransformations = 0

  // Pre-build lookup map for O(1) entity handle → typeId check
  const entityToTypeMap = new Map<string, string>()
  mappings.forEach(m => entityToTypeMap.set(m.entityHandle, m.typeId))

  cells.forEach(cell => {
    if (!cell.isElement()) return
    const element = cell as dia.Element

    const elementTypeId = element.prop('objectTypeId')
    const elementEntityHandle = element.prop('data/entityHandle')

    let shouldUpdate = false

    if (elementTypeId === typeId) {
      shouldUpdate = true
    } else if (elementEntityHandle) {
      shouldUpdate = entityToTypeMap.get(elementEntityHandle) === typeId
    }

    if (shouldUpdate) {
      const currentAttrs = element.attr()

      // Determine if we're updating color or image
      const isActualAsset = type.icon && (
        type.icon.startsWith('/assets/') ||
        type.icon.startsWith('http') ||
        type.icon.startsWith('data:image/')  // Support base64 images
      )
      // Detect if element type needs to change
      const isCurrentlyImage = currentAttrs.image !== undefined
      const shouldBeImage = isActualAsset
      const needsTypeChange = isCurrentlyImage !== shouldBeImage

      if (needsTypeChange) {
        // Element type transformation required

        // Extract element properties
        const position = element.position()
        const size = element.size()
        const zIndex = element.get('z') as number
        const elementId = element.id
        const data = element.prop('data')
        const objectTypeId = element.prop('objectTypeId')

        // Create new element with correct type
        let newElement: dia.Element

        if (shouldBeImage) {
          // Color → Image transformation
          const iconW = type.iconWidth || 32
          const iconH = type.iconHeight || 32

          // Calculate centered position for icon
          const centeredX = position.x + (size.width - iconW) / 2
          const centeredY = position.y + (size.height - iconH) / 2

          newElement = new shapes.standard.Image({
            id: elementId,
            position: { x: centeredX, y: centeredY },
            size: { width: iconW, height: iconH },
            z: zIndex,
            attrs: {
              image: { xlinkHref: type.icon },
              label: { text: '' }
            },
            data
          })

        } else {
          // Image → Color transformation
          const cm = type.colorMode || 'fill'

          // Get original path data if available, otherwise create a rectangle
          const bodyAttrs = currentAttrs.body
          const pathData = bodyAttrs?.d || `M 0 0 L ${size.width} 0 L ${size.width} ${size.height} L 0 ${size.height} Z`

          newElement = new shapes.standard.Path({
            id: elementId,
            position: position,
            size: size,
            z: zIndex,
            attrs: {
              body: {
                d: pathData,
                fill: cm === 'stroke' ? 'none' : type.color,
                stroke: type.color,
                strokeWidth: cm === 'stroke' ? 2 : 0,
                opacity: 0.8
              },
              label: { text: '' }
            },
            data
          })

        }

        // Preserve objectTypeId for future sync
        if (objectTypeId) {
          newElement.prop('objectTypeId', objectTypeId)
        }

        // Replace element in graph
        graph.removeCells([element])
        graph.addCell(newElement)

        typeTransformations++
        updatedCount++
      } else {
        // Same type - just update attributes
        if ((type.color || type.strokeColor || type.fillColor) && !currentAttrs.image) {
          let fillColor: string
          let strokeColor: string
          let sw: number
          if (type.strokeColor || type.fillColor) {
            strokeColor = type.strokeColor || type.color || '#333'
            fillColor = type.fillColor === 'none' ? 'none' : (type.fillColor || type.color || '#4CAF50')
            sw = type.strokeWidth ?? 2
          } else {
            const cm = type.colorMode || 'fill'
            fillColor = cm === 'stroke' ? 'none' : type.color!
            strokeColor = type.color!
            sw = cm === 'stroke' ? 2 : 0
          }

          const shapeKey = ['body', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'path'].find(k => currentAttrs[k])
          if (shapeKey) {
            element.attr(`${shapeKey}/fill`, fillColor)
            element.attr(`${shapeKey}/stroke`, strokeColor)
            element.attr(`${shapeKey}/strokeWidth`, sw)
            element.attr(`${shapeKey}/opacity`, type.opacity ?? 0.8)
            colorUpdates++
          }
        }

        // Update icon for image elements (only if icon is a URL)
        if (isActualAsset && currentAttrs.image) {
          element.attr('image/xlinkHref', type.icon)
          imageUpdates++
        }

        updatedCount++
      }
    }
  })


  // Note: JointJS automatically re-renders views when element.attr() is called
  // No manual paper update needed - the changes are automatically reflected
}

/**
 * Update a single element's color or icon based on its object type.
 * Lighter version of updateElementsForType for indexed lookup.
 */
function updateSingleElement(
  element: dia.Element,
  type: { name: string; color?: string; icon?: string; iconWidth?: number; iconHeight?: number; colorMode?: string; strokeColor?: string; fillColor?: string; opacity?: number; strokeWidth?: number }
) {
  const currentAttrs = element.attr()

  const isActualAsset = type.icon && (
    type.icon.startsWith('/assets/') ||
    type.icon.startsWith('http') ||
    type.icon.startsWith('data:image/')
  )
  const colorMode = type.colorMode || 'fill'

  // Color update (no type change)
  if (type.color && !currentAttrs.image) {
    const fillColor = colorMode === 'stroke' ? 'none' : type.color
    const strokeColor = type.color
    const strokeWidth = colorMode === 'stroke' ? 2 : 0

    const shapeKey = ['body', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'path'].find(k => currentAttrs[k])
    if (shapeKey) {
      element.attr(`${shapeKey}/fill`, fillColor)
      element.attr(`${shapeKey}/stroke`, strokeColor)
      element.attr(`${shapeKey}/strokeWidth`, strokeWidth)
    }
  }

  // Icon update for image elements (icon source + size)
  if (isActualAsset && currentAttrs.image) {
    element.attr('image/xlinkHref', type.icon)
    const w = type.iconWidth || 32
    const h = type.iconHeight || 32
    element.attr('image/width', w)
    element.attr('image/height', h)
    element.resize(w, h)
  }
}

/**
 * 객체의 타입이 바뀐 뒤 스타일을 새 타입에 맞춘다 — 색/아이콘 갱신, 필요하면
 * rect↔image 셰이프 교체(remove/add)까지. PropertyEditor·객체 속성 시트의
 * 타입 변경 경로(EditorPage handleObjectUpdate)에서 호출한다.
 * updateElementsForType 는 objectTypeId prop 이 typeId 인 요소만 만지므로
 * 같은 타입의 다른 요소를 다시 칠해도 결과는 동일(멱등)하다.
 */
export function applyTypeStyle(
  graph: dia.Graph,
  typeId: string,
  type: { name: string; color?: string; icon?: string; iconWidth?: number; iconHeight?: number; colorMode?: string; strokeColor?: string; fillColor?: string; opacity?: number; strokeWidth?: number }
) {
  updateElementsForType(graph, null as unknown as dia.Paper, typeId, type, [])
}
